import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import {
  getEventBonuses,
  createBonus,
  updateBonus,
  deleteBonus
} from '../../services/landingPageService';

const { FiPlus, FiEdit2, FiTrash2, FiGift, FiX, FiDollarSign, FiExternalLink } = FiIcons;

export default function BonusesTab({ eventId }) {
  const [bonuses, setBonuses] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingBonus, setEditingBonus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadBonuses();
  }, [eventId]);

  async function loadBonuses() {
    try {
      setLoading(true);
      const { data, error } = await getEventBonuses(eventId);
      if (error) throw error;
      setBonuses(data || []);
    } catch (error) {
      console.error('[BonusesTab] Error loading bonuses:', error);
      toast.error('Failed to load bonuses');
    } finally {
      setLoading(false);
    }
  }

  function handleEdit(bonus) {
    setEditingBonus(bonus);
    setShowModal(true);
  }

  function handleAdd() {
    setEditingBonus(null);
    setShowModal(true);
  }

  async function handleDelete(bonusId) {
    if (!window.confirm('Are you sure you want to delete this bonus?')) return;

    try {
      const { error } = await deleteBonus(bonusId);
      if (error) throw error;

      await loadBonuses();
      toast.success('Bonus deleted');
    } catch (error) {
      console.error('[BonusesTab] Error deleting bonus:', error);
      toast.error('Failed to delete bonus');
    }
  }

  async function handleSave(bonusData) {
    try {
      if (editingBonus) {
        const { error } = await updateBonus(editingBonus.id, bonusData);
        if (error) throw error;
        toast.success('Bonus updated');
      } else {
        const maxOrder = bonuses.reduce((max, b) => Math.max(max, b.display_order || 0), 0);
        const { error } = await createBonus({
          ...bonusData,
          event_id: eventId,
          display_order: maxOrder + 1
        });
        if (error) throw error;
        toast.success('Bonus created');
      }

      setShowModal(false);
      setEditingBonus(null);
      await loadBonuses();
    } catch (error) {
      console.error('[BonusesTab] Error saving bonus:', error);
      toast.error('Failed to save bonus');
    }
  }

  // Parse value_description (e.g. "$99" or "99") to number for display/total
  const parseBonusValue = (valueDescription) => {
    if (valueDescription == null || valueDescription === '') return 0;
    const num = parseFloat(String(valueDescription).replace(/[$,]/g, '').trim());
    return Number.isFinite(num) ? num : 0;
  };
  const totalValue = bonuses.reduce((sum, bonus) => sum + parseBonusValue(bonus.value_description), 0);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Bonuses & Resources</h2>
          <p className="text-sm text-gray-500 mt-1">
            Add valuable bonuses included with registration
          </p>
        </div>
        <button
          onClick={handleAdd}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
        >
          <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />
          Add Bonus
        </button>
      </div>

      {/* Total Value Summary */}
      {bonuses.length > 0 && totalValue > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <SafeIcon icon={FiDollarSign} className="w-5 h-5 text-green-600 mr-2" />
              <span className="text-green-800 font-medium">Total Bonus Value</span>
            </div>
            <span className="text-2xl font-bold text-green-600">
              ${totalValue.toLocaleString()}
            </span>
          </div>
        </div>
      )}

      {/* Bonuses List */}
      <div className="space-y-4">
        {bonuses.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg border-2 border-dashed border-gray-300">
            <SafeIcon icon={FiGift} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No bonuses added yet</p>
            <button
              onClick={handleAdd}
              className="mt-4 text-blue-600 hover:text-blue-700 font-medium"
            >
              Add your first bonus
            </button>
          </div>
        ) : (
          bonuses.map((bonus, index) => (
            <div key={bonus.id} className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <SafeIcon icon={FiGift} className="w-6 h-6 text-purple-600" />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-semibold text-gray-900">{bonus.bonus_title}</h4>
                      {bonus.bonus_description && (
                        <p className="text-gray-600 mt-1 text-sm">{bonus.bonus_description}</p>
                      )}
                    </div>

                    {(parseBonusValue(bonus.value_description) > 0) && (
                      <span className="ml-4 text-lg font-bold text-green-600 whitespace-nowrap">
                        ${parseBonusValue(bonus.value_description).toLocaleString()} value
                      </span>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-2 mt-3">
                    {bonus.bonus_type && (
                      <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded">
                        {bonus.bonus_type}
                      </span>
                    )}
                    {bonus.delivery_method && (
                      <span className="text-xs px-2 py-1 bg-blue-50 text-blue-600 rounded">
                        {bonus.delivery_method}
                      </span>
                    )}
                    {bonus.access_url && (
                      <a
                        href={bonus.access_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs px-2 py-1 bg-gray-100 text-blue-600 rounded inline-flex items-center hover:bg-blue-50"
                      >
                        <SafeIcon icon={FiExternalLink} className="w-3 h-3 mr-1" />
                        Preview
                      </a>
                    )}
                  </div>
                </div>

                <div className="flex items-center space-x-1 ml-4">
                  <button
                    onClick={() => handleEdit(bonus)}
                    className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Edit"
                  >
                    <SafeIcon icon={FiEdit2} className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(bonus.id)}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <BonusModal
          bonus={editingBonus}
          onSave={handleSave}
          onClose={() => {
            setShowModal(false);
            setEditingBonus(null);
          }}
        />
      )}
    </div>
  );
}

function BonusModal({ bonus, onSave, onClose }) {
  const [formData, setFormData] = useState({
    bonus_title: bonus?.bonus_title ?? '',
    bonus_description: bonus?.bonus_description ?? '',
    value_description: bonus?.value_description ?? '',
    bonus_type: bonus?.bonus_type ?? 'resource',
    delivery_method: bonus?.delivery_method ?? 'digital',
    access_url: bonus?.access_url ?? '',
    icon_name: bonus?.icon_name ?? ''
  });
  const [saving, setSaving] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();

    if (!formData.bonus_title.trim()) {
      toast.error('Title is required');
      return;
    }

    setSaving(true);
    await onSave(formData);
    setSaving(false);
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4 my-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {bonus ? 'Edit Bonus' : 'Add Bonus'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 max-h-[60vh] overflow-y-auto">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title *
            </label>
            <input
              type="text"
              value={formData.bonus_title}
              onChange={(e) => setFormData({ ...formData, bonus_title: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g., Investment Calculator Template"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.bonus_description}
              onChange={(e) => setFormData({ ...formData, bonus_description: e.target.value })}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Describe the bonus and its benefits..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Value description
              </label>
              <input
                type="text"
                value={formData.value_description}
                onChange={(e) => setFormData({ ...formData, value_description: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g. $99 or 99"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type
              </label>
              <select
                value={formData.bonus_type}
                onChange={(e) => setFormData({ ...formData, bonus_type: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="resource">Resource</option>
                <option value="template">Template</option>
                <option value="ebook">E-book</option>
                <option value="video">Video</option>
                <option value="course">Course</option>
                <option value="software">Software</option>
                <option value="consultation">Consultation</option>
                <option value="community">Community Access</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Delivery Method
            </label>
            <select
              value={formData.delivery_method}
              onChange={(e) => setFormData({ ...formData, delivery_method: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="digital">Digital Download</option>
              <option value="email">Email</option>
              <option value="portal">Member Portal</option>
              <option value="physical">Physical Delivery</option>
              <option value="live">Live Session</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Access/Preview URL
            </label>
            <input
              type="url"
              value={formData.access_url}
              onChange={(e) => setFormData({ ...formData, access_url: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="https://..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Icon name
            </label>
            <input
              type="text"
              value={formData.icon_name}
              onChange={(e) => setFormData({ ...formData, icon_name: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g. FiGift or icon identifier"
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving...' : bonus ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
